/**
 * useOnboardingState — onboarding-mode derivation for the AI Actions list.
 *
 * Owns the onboarding status query, the cycling "Setting things up…"
 * messages, and the oscillation guards that keep onboarding mode stable
 * while React Query serves stale data from differently-keyed list queries
 * during refetches.
 */
import { useMemo, useRef } from 'react';

import { useOnboardingStatusQuery } from '@/hooks/useOnboardingStatusQuery';
import type {
  ProposalListItem,
  ProposalsListResponse,
} from '@/lib/proposals/core/types';
import {
  isOnboardingProposal,
  isWelcomeProposal,
} from '@/lib/proposals/utils/onboardingConfig';

import { getGroupedLabels } from '../onboarding-step-labels';
import { getOnboardingSortPriority } from '../shared/utils';

// Mirrors GeneratingActionsIndicator's own internal default — kept local
// (rather than imported) so that file only exports the component.
const DEFAULT_GENERATING_MESSAGE = ['Setting things up…'];

interface UseOnboardingStateArgs {
  orgId: number | undefined;
  proposalsResponse: ProposalsListResponse | undefined;
  isFetching: boolean;
}

export function useOnboardingState({
  orgId,
  proposalsResponse,
  isFetching,
}: UseOnboardingStateArgs) {
  // Onboarding status — used to derive cycling messages from real step labels
  const { query: onboardingStatusQuery } = useOnboardingStatusQuery(orgId);
  const onboardingStatus = onboardingStatusQuery.data;

  // Derive cycling messages from pending/in-progress onboarding steps.
  // Memoize on a stable string key (sorted step names) so the array identity
  // doesn't change on every React Query refetch when the data is the same.
  const inProgressKey = useMemo(() => {
    if (!onboardingStatus?.steps) {
      return '';
    }
    return onboardingStatus.steps
      .filter(s => s.status === 'in_progress')
      .map(s => s.step_name)
      .sort()
      .join(',');
  }, [onboardingStatus?.steps]);

  const onboardingMessages = useMemo(() => {
    if (!inProgressKey) {
      return DEFAULT_GENERATING_MESSAGE;
    }
    const names = inProgressKey.split(',');
    const pendingLabels = getGroupedLabels(names).map(label => `${label}…`);
    return pendingLabels.length > 0
      ? pendingLabels
      : DEFAULT_GENERATING_MESSAGE;
  }, [inProgressKey]);

  // The first-actions step parks in WAITING until the user creates a deal
  // pipeline. The generating-indicator renders this as a distinct informational
  // state rather than the "generating" spinner.
  const isWaitingForPipeline = useMemo(
    () =>
      (onboardingStatus?.steps ?? []).some(
        s => s.step_name === 'create_proposals' && s.status === 'waiting'
      ),
    [onboardingStatus?.steps]
  );

  // Track whether we've ever received data (survives refetches caused by switching
  // view segments or filters)
  // This prevents flashing back to skeleton during segment/filter-triggered refetches
  const hasEverLoadedRef = useRef(false);

  // Track the confirmed onboarding mode - only updated when we have FRESH data
  // This prevents oscillation when switching view segments/filters (different cache keys may have stale data)
  const confirmedOnboardingModeRef = useRef<boolean | null>(null);

  // Track if we're transitioning out of onboarding mode to prevent oscillation
  const isTransitioningFromOnboardingRef = useRef(false);

  // Backend onboarding count + the action-less welcome proposal, which carries
  // no 'onboarding' filter tag and so is absent from filter_tag_counts. Adding it
  // here lets onboarding mode (and the pinned section) engage even when the
  // welcome placeholder is the only onboarding item in the list.
  const onboardingCount =
    (proposalsResponse?.filter_tag_counts?.onboarding ?? 0) +
    ((proposalsResponse?.results ?? []).some(isWelcomeProposal) ? 1 : 0);

  // Only update refs when we have FRESH data (not stale data during refetch)
  // The key fix: check !isFetching to avoid updating with stale cached data
  // from different query keys (e.g., 'all' vs 'onboarding' filtered queries)
  if (proposalsResponse !== undefined && !isFetching) {
    hasEverLoadedRef.current = true;

    // Once we transition to non-onboarding mode, don't flip back
    // This prevents oscillation when 'all' query has stale cached data
    if (isTransitioningFromOnboardingRef.current) {
      // Stay in non-onboarding mode once we've transitioned
      if (onboardingCount === 0) {
        confirmedOnboardingModeRef.current = false;
        isTransitioningFromOnboardingRef.current = false;
      }
      // If onboarding count is > 0 during transition, ignore (stale data from other query)
    } else {
      const wasOnboarding = confirmedOnboardingModeRef.current === true;
      const wasNotOnboarding = confirmedOnboardingModeRef.current === false;
      const nowOnboarding = onboardingCount > 0;

      if (wasOnboarding && !nowOnboarding) {
        // Start transition - lock in non-onboarding mode
        isTransitioningFromOnboardingRef.current = true;
        confirmedOnboardingModeRef.current = false;
      } else if (wasNotOnboarding && nowOnboarding) {
        // New onboarding actions appeared - reset transition guard to allow onboarding mode
        isTransitioningFromOnboardingRef.current = false;
        confirmedOnboardingModeRef.current = true;
      } else {
        confirmedOnboardingModeRef.current = nowOnboarding;
      }
    }
  }

  // Use confirmed mode from ref (survives refetches where onboardingCount temporarily becomes 0)
  // Default to false only during true initial load
  // Note: isOnboardingMode no longer takes over the UI — it just controls whether
  // the onboarding section appears at the top and non-onboarding actions are disabled
  const isOnboardingMode = confirmedOnboardingModeRef.current ?? false;

  // Onboarding category data for the pinned section
  const onboardingCategory = useMemo(() => {
    const results = proposalsResponse?.results;
    if (!results) {
      return undefined;
    }
    const onboarding = results.filter(isOnboardingProposal);
    if (onboarding.length === 0) {
      return undefined;
    }
    const actions = [...onboarding].sort(
      (a: ProposalListItem, b: ProposalListItem) =>
        getOnboardingSortPriority(a) - getOnboardingSortPriority(b)
    );
    return { actions };
  }, [proposalsResponse?.results]);

  // Show pinned onboarding section when in onboarding mode
  const showPinnedOnboarding = Boolean(isOnboardingMode && onboardingCategory);

  // Onboarding pipeline is still running (steps not all complete)
  // Fall back to onboardingCount when status data is loading to prevent flash of empty state
  const isOnboardingActive =
    onboardingStatus != null
      ? !onboardingStatus.pipeline_complete
      : onboardingCount > 0;

  return {
    onboardingStatus,
    onboardingMessages,
    isWaitingForPipeline,
    onboardingCount,
    onboardingCategory,
    showPinnedOnboarding,
    isOnboardingActive,
    hasEverLoadedRef,
  };
}
